/**
 * Unified SVG-based Map Marker for both single and multi-tier countries
 */

import { getTierColor } from '../../../services/TierColorService';
import { 
  calculateTierDistribution, 
  generatePieSlices, 
  hasMultipleTiers,
  getDominantTier,
  createSingleTierCircleSvg
} from './pieChartUtils';

/**
 * Class to create and manage unified SVG-based map markers
 */
class UnifiedMarker {
  /**
   * Create a new unified map marker
   * @param {Object} options - Configuration options
   * @param {Object} options.country - Country data
   * @param {number} options.maxTotal - Maximum total value for all countries
   * @param {number} options.maxLargest - Maximum largest value for all countries
   * @param {number} options.zoom - Current map zoom level
   * @param {Object} options.L - Leaflet library instance
   * @param {Object} options.layer - Map layer to add the marker to
   * @param {string|null} options.activeTierFilter - Active tier filter for color changes
   * @param {boolean} options.showTimezones - Whether timezone information should be shown
   * @param {Array} options.clientData - Client data for tier calculations
   * @param {boolean} options.useTierColors - Whether to use tier-based colors
   * @param {Function} options.onSelect - Callback when marker is selected
   */
  constructor({ country, maxTotal, maxLargest, zoom, L, layer, activeTierFilter, showTimezones, clientData, useTierColors, onSelect }) {
    this.country = country;
    this.maxTotal = maxTotal;
    this.maxLargest = maxLargest;
    this.zoom = zoom;
    this.L = L;
    this.layer = layer;
    this.activeTierFilter = activeTierFilter;
    this.showTimezones = showTimezones;
    this.clientData = clientData;
    this.useTierColors = useTierColors;
    this.onSelect = onSelect;
    
    // Calculate tier distribution for this country
    this.tierDistribution = calculateTierDistribution(clientData, country.country);
    this.isMultiTier = hasMultipleTiers(clientData, country.country);
    this.dominantTier = getDominantTier(clientData, country.country);
    
    // Set colors based on tier filtering or tier coloring mode
    if (activeTierFilter) {
      // Use tier-specific colors when tier filtering is active
      this.colors = {
        light: getTierColor(activeTierFilter, 'light'),
        dark: getTierColor(activeTierFilter, 'dark')
      };
    } else if (useTierColors) {
      // Use tier colors based on the dominant tier for this country
      this.colors = {
        light: getTierColor(this.dominantTier, 'light'),
        dark: getTierColor(this.dominantTier, 'dark')
      };
    } else {
      // Default blue colors when no tier filtering or tier coloring
      this.colors = {
        light: '#b3d9ff', // Light blue for outer circle
        dark: '#0074D9'   // Medium blue for inner circle
      };
    }
  }
  
  /**
   * Calculate the radius for a circle based on value
   * @param {number} value - Value to scale
   * @param {number} maxValue - Maximum value for scaling
   * @param {number} minRadius - Minimum radius
   * @param {number} scale - Scale factor
   * @returns {number} Calculated radius
   */
  calcRadius(value, maxValue, minRadius, scale) {
    const base = minRadius + (value / maxValue) * scale;
    
    // Progressive scaling to prevent map clutter at low zoom levels
    let effZ;
    let adjustedMinRadius = minRadius;
    
    if (this.zoom >= 3) {
      effZ = 3; // Normal scaling for zoom 3+
    } else if (this.zoom === 2) {
      effZ = 20; // Very small circles for world view
      adjustedMinRadius = Math.min(minRadius, 2);
    } else {
      effZ = 40; // Extremely tiny circles for maximum zoom out
      adjustedMinRadius = Math.min(minRadius, 1);
    }
    
    const factor = 2 / Math.max(effZ, 1);
    return Math.max(adjustedMinRadius, Math.min(base * factor, scale));
  }
  
  /**
   * Get timezone information for this country from client data
   * @returns {Object} Timezone information
   */
  getCountryTimezone() {
    if (!this.clientData || !this.clientData.length) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Find clients from this country
    const countryClients = this.clientData.filter(client => 
      client.country === this.country.country
    );
    
    if (countryClients.length === 0) {
      return { timezone: 'N/A', localTime: 'N/A' };
    }
    
    // Get timezone from the first client
    const firstClient = countryClients[0];
    const timezone = firstClient['Time zone'] || 'N/A';
    
    // Calculate local time if we have timezone info
    let localTime = 'N/A';
    if (timezone && timezone !== 'N/A') {
      try {
        const now = new Date();
        if (timezone.startsWith('UTC')) {
          const offsetStr = timezone.substring(3);
          if (offsetStr) {
            const offsetHours = parseFloat(offsetStr);
            const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
            const localTimeMs = utcTime + (offsetHours * 3600000);
            const localDate = new Date(localTimeMs);
            
            localTime = localDate.toLocaleString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
              weekday: 'short'
            });
          }
        }
      } catch (error) {
        console.warn('Error calculating local time:', error);
        localTime = 'Time unavailable';
      }
    }
    
    return { timezone, localTime };
  }
  
  /**
   * Create tier breakdown HTML for popup (only for multi-tier countries)
   * @returns {string} HTML content for tier breakdown
   */
  createTierBreakdownHtml() {
    if (!this.isMultiTier || !this.tierDistribution.tiers || this.tierDistribution.tiers.length === 0) {
      return '';
    }
    
    let breakdownHtml = `
      <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
      <p style="margin: 3px 0 6px 0; font-size: 13px; line-height: 1.3; font-weight: 600;">Tier Breakdown:</p>
    `;
    
    this.tierDistribution.tiers.forEach(({ tier, count, percentage, formattedProfit }) => {
      const tierColor = getTierColor(tier, 'dark');
      breakdownHtml += `
        <p style="margin: 2px 0; font-size: 13px; line-height: 1.3;">
          <span style="display: inline-block; width: 12px; height: 12px; background-color: ${tierColor}; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>
          <strong>Tier ${tier}:</strong> ${count} clients (${percentage.toFixed(1)}%) - ${formattedProfit}
        </p>
      `;
    });
    
    return breakdownHtml;
  }
  
  /**
   * Create popup HTML content
   * @returns {string} HTML content for popup
   */
  createPopupContent() {
    const { country, dominantManager, managers, total, largest } = this.country;
    const others = managers.filter(m => m !== dominantManager);
    
    let popupHtml = `
      <div class="info-popup">
        <h3 style="margin: 0 0 8px 0; font-size: 16px; color: #333; font-weight: 600;">${country}</h3>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Total Groups:</strong> ${total}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Group:</strong> ${largest}</p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Largest Manager:</strong> <span style="color:#0074D9; font-weight:bold;">${dominantManager}</span></p>
    `;
    
    if (others.length) {
      popupHtml += `<p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Other Managers:</strong> ${others.join(', ')}</p>`;
    }
    
    // Add tier breakdown for multi-tier countries
    popupHtml += this.createTierBreakdownHtml();
    
    // Add timezone information if Show Zones is active
    if (this.showTimezones) {
      const timezoneInfo = this.getCountryTimezone();
      popupHtml += `
        <hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Timezone:</strong> <span style="color: #0074D9; font-weight: bold;">${timezoneInfo.timezone}</span></p>
        <p style="margin: 3px 0; font-size: 13px; line-height: 1.3;"><strong>Local Time:</strong> ${timezoneInfo.localTime}</p>
      `;
    }
    
    popupHtml += `</div>`;
    return popupHtml;
  }
  
  /**
   * Create SVG pie chart icon for multi-tier countries with perfect circular appearance
   * @param {number} radius - Radius of the pie chart
   * @returns {string} SVG string for the pie chart
   */
  createPieChartSvg(radius) {
    const slices = generatePieSlices(this.tierDistribution.tiers, radius);
    const size = radius * 2;
    const centerX = radius;
    const centerY = radius;
    
    // Create unique ID for this marker to avoid conflicts
    const uniqueId = `${this.country.country.replace(/\s+/g, '')}-${radius}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create SVG with anti-aliasing and high-quality rendering
    let svgContent = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg" style="shape-rendering: geometricPrecision;">`;
    
    // Add definitions for perfect circular clipping and gradients
    svgContent += `
      <defs>
        <clipPath id="circleClip-${uniqueId}">
          <circle cx="${centerX}" cy="${centerY}" r="${radius - 1}"/>
        </clipPath>
    `;
    
    // Add subtle gradients for each tier to enhance visual quality
    slices.forEach(({ tier }, index) => {
      const baseColor = this.activeTierFilter 
        ? getTierColor(this.activeTierFilter, 'dark')
        : getTierColor(tier, 'dark');
      
      // Create a subtle radial gradient for depth
      svgContent += `
        <radialGradient id="tierGradient-${tier}-${uniqueId}" cx="50%" cy="30%" r="70%">
          <stop offset="0%" style="stop-color:${this.lightenColor(baseColor, 0.1)};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${baseColor};stop-opacity:1" />
        </radialGradient>
      `;
    });
    
    svgContent += `</defs>`;
    
    // Create a perfect background circle first
    svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${radius - 1}" fill="#f8f9fa" stroke="none"/>`;
    
    // Group all slices with circular clipping for perfect edges
    svgContent += `<g clip-path="url(#circleClip-${uniqueId})">`;
    
    // Add each pie slice with enhanced rendering
    slices.forEach(({ tier, path }, index) => {
      const fillColor = this.activeTierFilter 
        ? getTierColor(this.activeTierFilter, 'dark')
        : getTierColor(tier, 'dark');
      
      // Use gradient fill for better visual quality
      const gradientId = `tierGradient-${tier}-${uniqueId}`;
      svgContent += `<path d="${path}" fill="url(#${gradientId})" stroke="none" style="shape-rendering: geometricPrecision;"/>`;
    });
    
    svgContent += '</g>';
    
    // Add a perfect circular border on top to ensure completely smooth circumference
    svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${radius - 1}" fill="none" stroke="#2c3e50" stroke-width="1.5" style="shape-rendering: geometricPrecision;" opacity="0.8"/>`;
    
    // Add a subtle inner highlight for depth
    svgContent += `<circle cx="${centerX}" cy="${centerY}" r="${radius - 2}" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="0.5" style="shape-rendering: geometricPrecision;"/>`;
    
    svgContent += '</svg>';
    return svgContent;
  }
  
  /**
   * Lighten a hex color by a given amount
   * @param {string} color - Hex color string
   * @param {number} amount - Amount to lighten (0-1)
   * @returns {string} Lightened hex color
   */
  lightenColor(color, amount) {
    const usePound = color[0] === '#';
    const col = usePound ? color.slice(1) : color;
    const num = parseInt(col, 16);
    let r = (num >> 16) + amount * 255;
    let g = (num >> 8 & 0x00FF) + amount * 255;
    let b = (num & 0x0000FF) + amount * 255;
    r = r > 255 ? 255 : r < 0 ? 0 : r;
    g = g > 255 ? 255 : g < 0 ? 0 : g;
    b = b > 255 ? 255 : b < 0 ? 0 : b;
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  }
  
  /**
   * Create SVG marker (pie chart for multi-tier, circle for single-tier)
   * @param {number} radius - Radius of the marker
   * @returns {string} SVG string for the marker
   */
  createMarkerSvg(radius) {
    if (this.isMultiTier) {
      return this.createPieChartSvg(radius);
    } else {
      // Create single-tier circle that matches pie chart style
      // When in pie chart mode, always use tier colors for single-tier countries
      const fillColor = this.activeTierFilter 
        ? getTierColor(this.activeTierFilter, 'dark')
        : getTierColor(this.dominantTier, 'dark'); // Always use tier color in pie chart mode
      
      return createSingleTierCircleSvg(radius, fillColor);
    }
  }
  
  /**
   * Add the unified marker to the map
   */
  addToMap() {
    const { L, layer, country, maxTotal, onSelect } = this;
    const { latitude, longitude } = country;
    
    // Calculate marker radius based on total clients
    const markerRadius = this.calcRadius(country.total, maxTotal, 8, 25);
    
    // Create SVG marker (pie chart or circle)
    const svgString = this.createMarkerSvg(markerRadius);
    const svgUrl = 'data:image/svg+xml;base64,' + btoa(svgString);
    
    // Create custom icon
    const markerIcon = L.icon({
      iconUrl: svgUrl,
      iconSize: [markerRadius * 2, markerRadius * 2],
      iconAnchor: [markerRadius, markerRadius],
      popupAnchor: [0, -markerRadius]
    });
    
    // Create marker
    const marker = L.marker([latitude, longitude], { icon: markerIcon }).addTo(layer);
    
    // Create popup
    const popupContent = this.createPopupContent();
    const popup = L.popup().setContent(popupContent);
    
    // Bind popup and click handler
    marker.bindPopup(popup);
    marker.on('click', onSelect);
    
    // Store marker reference
    this.marker = marker;
    
    return this;
  }
  
  /**
   * Remove the marker from the map
   */
  remove() {
    if (this.marker) {
      this.marker.remove();
    }
  }
}

export default UnifiedMarker;
